import React from 'react';
import { Pause, X, Music } from 'lucide-react';
import { useMusicPreview } from './useMusicPreview';

// MiniMusicPlayer — a calm, persistent preview controller.
//
// Renders ONLY when a track is currently playing via the singleton
// `useMusicPreview` store. Floats at the bottom-right on mobile (with
// safe-area inset) and inline-anchored within its parent on desktop.
// No progress bar, no full controls — just a small pause/close.
//
// User explicitly asked for: persistent playback, single track at a time,
// no autoplay, no full player.

export function MiniMusicPlayer() {
  const { playing, pause } = useMusicPreview();
  if (!playing) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="music-mini-player"
      style={{
        // Stay above on-screen keyboards and respect mobile safe areas
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
        boxShadow:
          '0 22px 40px -22px rgba(26,54,38,0.45), 0 6px 14px -8px rgba(26,54,38,0.30), inset 0 1px 0 rgba(255,255,255,0.85)',
      }}
      className="fixed right-4 sm:right-6 z-30 flex items-center gap-3 pl-3 pr-2 py-2 rounded-full bg-gradient-to-b from-[hsl(var(--surface))] to-[hsl(var(--background))] border border-[hsl(var(--primary)/0.18)] max-w-[calc(100vw-2rem)]"
    >
      {/* Tiny breathing indicator dot */}
      <span
        aria-hidden
        className="shrink-0 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse"
      />

      <Music
        size={13}
        strokeWidth={1.5}
        className="shrink-0 text-[hsl(var(--primary)/0.55)]"
        aria-hidden
      />

      <div className="min-w-0 flex flex-col leading-tight">
        <span
          className="font-serif text-[13px] text-[hsl(var(--primary))] truncate"
          data-testid="music-mini-player-name"
        >
          {playing.name}
        </span>
        <span className="text-[9px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.5)] truncate">
          {playing.mood} · Previewing
        </span>
      </div>

      <button
        type="button"
        onClick={pause}
        aria-label={`Pause ${playing.name}`}
        data-testid="music-mini-player-pause"
        className="shrink-0 w-8 h-8 ml-1 rounded-full border border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-white flex items-center justify-center hover:bg-[hsl(var(--accent)/0.92)] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/50"
      >
        <Pause size={13} strokeWidth={2} />
      </button>

      <button
        type="button"
        onClick={pause}
        aria-label="Stop preview"
        data-testid="music-mini-player-close"
        className="shrink-0 w-7 h-7 -mr-0.5 rounded-full text-[hsl(var(--primary)/0.5)] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.06)] flex items-center justify-center"
      >
        <X size={12} strokeWidth={2} />
      </button>
    </div>
  );
}

export default MiniMusicPlayer;
